import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Wifi, WifiOff, Loader2,
  Send, CheckCircle2, XCircle, BookOpen,
  Users, Zap, MessageCircle, Mail, Smartphone,
  TrendingUp, Radio, RefreshCw, FlaskConical,
} from 'lucide-react';
import { useCampaign } from '../hooks/useCampaigns';
import { useWarRoom, type LiveEvent } from '../hooks/useWarRoom';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import { useSendCampaign, useIsTestMode } from '../hooks/useSendCampaign';

/* ── Ring chart (SVG puro, sem dependência) ──────────────────────────────────── */

function RingChart({
  value, total, label,
}: { value: number; total: number; label: string }) {
  const radius       = 52;
  const stroke       = 13;
  const circumference = 2 * Math.PI * radius;
  const pct          = total > 0 ? Math.min(1, value / total) : 0;
  const arc          = pct * circumference;

  return (
    <div className="relative flex items-center justify-center shrink-0">
      <svg width={136} height={136} viewBox="0 0 136 136" className="-rotate-90">
        {/* Trilha */}
        <circle
          cx={68} cy={68} r={radius}
          fill="none"
          stroke="color-mix(in oklab, var(--color-ink), transparent 88%)"
          strokeWidth={stroke}
        />
        {/* Progresso */}
        <circle
          cx={68} cy={68} r={radius}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${arc} ${circumference}`}
          style={{ transition: 'stroke-dasharray 0.7s cubic-bezier(.4,0,.2,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="font-display text-3xl font-extrabold text-ink tabular-nums leading-none">
          {total > 0 ? `${Math.round(pct * 100)}%` : '—'}
        </span>
        <span className="text-[10px] text-ink-subtle mt-1">{label}</span>
      </div>
    </div>
  );
}

/* ── Metric card ─────────────────────────────────────────────────────────────── */

function MetricCard({
  label, value, icon: Icon, color = 'text-ink-muted', highlight = false,
}: {
  label: string; value: number;
  icon: React.ElementType; color?: string; highlight?: boolean;
}) {
  return (
    <div className={`rounded-2xl card-premium p-4 flex flex-col gap-2 ${highlight ? 'border-accent/20 shadow-glow-xs' : ''}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-ink-subtle leading-none">
          {label}
        </span>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <span className="font-display text-2xl font-extrabold text-ink tabular-nums">
        {value.toLocaleString('pt-BR')}
      </span>
    </div>
  );
}

/* ── Estilos por status do feed ─────────────────────────────────────────────── */

const FEED_STYLE: Record<LiveEvent['status'], { label: string; color: string; bg: string }> = {
  sent:      { label: 'Enviado',  color: 'text-blue-300',    bg: 'bg-blue-500/12'    },
  delivered: { label: 'Entregue', color: 'text-emerald-300', bg: 'bg-emerald-500/12' },
  read:      { label: 'Lido',     color: 'text-accent',      bg: 'bg-accent/12'      },
  failed:    { label: 'Falha',    color: 'text-danger',      bg: 'bg-danger/12'      },
};

function maskPhone(phone: string): string {
  if (!phone || phone.length < 6) return phone;
  const last4 = phone.slice(-4);
  return `${'•'.repeat(Math.max(0, phone.length - 4))}${last4}`;
}

/* ── Barra de progresso multi-segmento ─────────────────────────────────────── */

function ProgressSegments({
  sent, delivered, read, failed, total,
}: { sent: number; delivered: number; read: number; failed: number; total: number }) {
  if (total === 0) return <div className="h-2 w-full rounded-full bg-midnight-4" />;

  const pSent      = Math.round((sent / total) * 100);
  const pDelivered = Math.round((delivered / total) * 100);
  const pRead      = Math.round((read / total) * 100);
  const pFailed    = Math.round((failed / total) * 100);

  return (
    <div className="h-2 w-full rounded-full bg-midnight-4 overflow-hidden flex">
      <div className="h-full bg-blue-400 transition-all duration-700"    style={{ width: `${pSent}%` }} />
      <div className="h-full bg-emerald-400 transition-all duration-700" style={{ width: `${pDelivered}%` }} />
      <div className="h-full bg-accent transition-all duration-700"      style={{ width: `${pRead}%` }} />
      <div className="h-full bg-danger transition-all duration-700"      style={{ width: `${pFailed}%` }} />
    </div>
  );
}

/* ── Página principal ────────────────────────────────────────────────────────── */

export function WarRoomPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const feedRef  = useRef<HTMLDivElement>(null);

  /* Controla o "Preparando envio…" quando o usuário chega antes do status mudar */
  const [preparingSince] = useState(() => Date.now());
  const [preparingTimeout, setPreparingTimeout] = useState(false);

  const { data: campaign, isLoading: campLoading } = useCampaign(id ?? null);

  const isActive = !campaign || ['running', 'paused', 'draft'].includes(campaign.status);
  const { stats, events, isConnected, throughput, isLoading } = useWarRoom(id, isActive);

  const retryMutation = useSendCampaign();
  const { data: isTestMode } = useIsTestMode(id, stats.sent > 0);

  /* Timeout de "preparando" — se draft por mais de 15 s sem progresso, avisa */
  useEffect(() => {
    if (campaign?.status !== 'draft') return;
    const t = setTimeout(() => setPreparingTimeout(true), 45_000);
    return () => clearTimeout(t);
  }, [campaign?.status]);

  /* Cancela o timeout se saiu de 'draft' */
  useEffect(() => {
    if (campaign?.status && campaign.status !== 'draft') setPreparingTimeout(false);
  }, [campaign?.status]);

  /* Auto-redireciona quando a campanha encerra */
  useEffect(() => {
    if (!campaign) return;
    if (['completed', 'failed'].includes(campaign.status)) {
      const t = setTimeout(() => void navigate(`/app/campanhas/${campaign.id}`), 3_500);
      return () => clearTimeout(t);
    }
  }, [campaign?.status, campaign?.id, navigate]);

  void preparingSince; // usado implicitamente no banner

  /* Feed rola para o topo em novos eventos */
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = 0;
  }, [events.length]);

  /* Loading */
  if (campLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <XCircle className="h-12 w-12 text-danger/60" />
        <p className="text-ink-muted">Campanha não encontrada.</p>
        <Link to="/app/campanhas" className="btn-ghost px-4 py-2 rounded-xl text-sm">
          Voltar
        </Link>
      </div>
    );
  }

  const TypeIcon = campaign.type === 'whatsapp' ? MessageCircle
    : campaign.type === 'email' ? Mail : Smartphone;

  const isRunning   = campaign.status === 'running';
  const isPaused    = campaign.status === 'paused';
  const isEnded     = ['completed', 'failed'].includes(campaign.status);
  const canRetry    = isEnded && stats.failed > 0 && !retryMutation.isPending;
  const isRetrying  = retryMutation.isPending;

  const handleRetry = () => {
    if (!id || isRetrying) return;
    retryMutation.mutate({ campaignId: id, retryFailed: true });
  };

  const sentPct      = stats.total > 0 ? Math.round((stats.sent / stats.total) * 100) : 0;
  const deliveryRate = stats.sent > 0 ? Math.round((stats.delivered / stats.sent) * 100) : 0;
  const readRate     = stats.sent > 0 ? Math.round((stats.read / stats.sent) * 100) : 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-16 animate-fade-in">

      {/* ── Topbar ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <Link
          to={`/app/campanhas/${campaign.id}`}
          className="flex items-center gap-1.5 rounded-xl btn-ghost px-3 py-2 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Campanha</span>
        </Link>
        <span className="text-ink-subtle">/</span>
        <span className="text-sm font-semibold text-ink truncate max-w-[180px]">{campaign.name}</span>
        <span className="text-ink-subtle">/</span>
        <span className="flex items-center gap-1.5 text-sm font-bold text-accent">
          <Radio className="h-3.5 w-3.5" />
          War Room
        </span>

        <div className="ml-auto flex items-center gap-2">
          {/* Badge de simulação */}
          {isTestMode && (
            <div className="flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold border text-amber-300 bg-amber-500/10 border-amber-500/20">
              <FlaskConical className="h-3 w-3" />
              <span className="hidden sm:inline">Simulação</span>
            </div>
          )}

          {/* Botão Reenviar falhas */}
          {canRetry && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold border cursor-pointer transition-colors text-danger bg-danger/10 border-danger/20 hover:bg-danger/20"
            >
              <RefreshCw className="h-3 w-3" />
              <span className="hidden sm:inline">Reenviar {stats.failed} falha{stats.failed !== 1 ? 's' : ''}</span>
            </button>
          )}

          {isRetrying && (
            <div className="flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold border text-ink-muted bg-midnight-3 border-hairline">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span className="hidden sm:inline">Reenviando…</span>
            </div>
          )}

          {/* Indicador Realtime */}
          <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold border transition-colors ${
            isConnected
              ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20'
              : 'text-ink-subtle bg-midnight-3 border-hairline'
          }`}>
            {isConnected
              ? <><Wifi className="h-3 w-3" /><span className="hidden sm:inline">Ao vivo</span></>
              : <><WifiOff className="h-3 w-3" /><span className="hidden sm:inline">Reconectando…</span></>
            }
          </div>
          <CampaignStatusBadge status={campaign.status} />
        </div>
      </div>

      {/* Banner de preparação (draft → running) */}
      {campaign?.status === 'draft' && !preparingTimeout && (
        <div className="flex items-center gap-3 rounded-xl bg-midnight-3 border border-hairline px-4 py-3 animate-fade-in">
          <Loader2 className="h-4 w-4 animate-spin text-accent shrink-0" />
          <p className="text-sm text-ink">Preparando o disparo — aguarde o início do envio…</p>
        </div>
      )}

      {/* Banner de timeout de preparação */}
      {campaign?.status === 'draft' && preparingTimeout && (
        <div className="flex items-center gap-3 rounded-xl bg-danger/10 border border-danger/20 px-4 py-3 animate-fade-in">
          <XCircle className="h-4 w-4 text-danger shrink-0" />
          <p className="text-sm text-ink flex-1">
            O disparo demorou mais do que o esperado. Verifique o status da campanha.
          </p>
          <Link
            to={`/app/campanhas/${campaign.id}`}
            className="text-xs font-semibold text-accent hover:underline shrink-0"
          >
            Ver campanha
          </Link>
        </div>
      )}

      {/* Banner de encerramento */}
      {isEnded && (
        <div className="flex items-center gap-3 rounded-xl bg-accent/10 border border-accent/20 px-4 py-3 animate-fade-in">
          <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
          <p className="text-sm text-ink">
            Campanha encerrada. Redirecionando para o relatório em instantes…
          </p>
          <Loader2 className="h-4 w-4 animate-spin text-accent ml-auto shrink-0" />
        </div>
      )}

      {/* Banner de pausa */}
      {isPaused && (
        <div className="flex items-center gap-3 rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-3">
          <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
          <p className="text-sm text-amber-200">Campanha pausada. O War Room permanece ativo.</p>
        </div>
      )}

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-3xl glass-strong shadow-deep p-6 md:p-8">
        {/* Aurora */}
        <div
          className="absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl pointer-events-none opacity-20"
          style={{ background: 'color-mix(in oklab, var(--color-accent), transparent 50%)' }}
        />

        <div className="relative flex flex-col sm:flex-row items-center gap-6">
          {/* Ring */}
          <RingChart value={stats.sent} total={stats.total} label="enviados" />

          {/* Info + progresso */}
          <div className="flex-grow w-full space-y-3 text-center sm:text-left">
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <TypeIcon className="h-5 w-5 text-accent shrink-0" />
              <h1 className="font-display text-xl md:text-2xl font-extrabold text-ink truncate">
                {campaign.name}
              </h1>
            </div>

            {/* Sub-métricas rápidas */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 justify-center sm:justify-start text-xs text-ink-muted">
              <span>
                <span className="font-semibold text-ink">{stats.total.toLocaleString('pt-BR')}</span> mensagens
              </span>
              <span>
                <span className="font-semibold text-emerald-300">{deliveryRate}%</span> entregues
              </span>
              <span>
                <span className="font-semibold text-accent">{readRate}%</span> lidos
              </span>
              {isRunning && throughput > 0 && (
                <span className="flex items-center gap-1 text-amber-300 font-semibold">
                  <Zap className="h-3 w-3" />
                  {throughput} msg/min
                </span>
              )}
            </div>

            {/* Barra multi-segmento */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[10px] font-mono text-ink-subtle">
                <span>{stats.sent.toLocaleString('pt-BR')} enviados de {stats.total.toLocaleString('pt-BR')}</span>
                <span>{sentPct}%</span>
              </div>
              <ProgressSegments
                sent={stats.sent}
                delivered={stats.delivered}
                read={stats.read}
                failed={stats.failed}
                total={stats.total}
              />
              {/* Legenda */}
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-ink-subtle">
                {[
                  { color: 'bg-blue-400',   label: 'Enviado'  },
                  { color: 'bg-emerald-400', label: 'Entregue' },
                  { color: 'bg-accent',     label: 'Lido'     },
                  { color: 'bg-danger',     label: 'Falha'    },
                ].map(({ color, label }) => (
                  <span key={label} className="flex items-center gap-1">
                    <span className={`h-2 w-2 rounded-full shrink-0 ${color}`} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Metric cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <MetricCard label="Público-alvo"  value={stats.total}     icon={Users}        color="text-blue-300"    />
        <MetricCard label="Enviados"       value={stats.sent}      icon={Send}         color="text-blue-400"    />
        <MetricCard label="Entregues"      value={stats.delivered} icon={CheckCircle2} color="text-emerald-400" />
        <MetricCard label="Lidos"          value={stats.read}      icon={BookOpen}     color="text-accent" highlight />
        <MetricCard label="Falhas"         value={stats.failed}    icon={XCircle}      color="text-danger"      />
      </div>

      {/* ── Breakdown + Feed ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* Breakdown de entrega */}
        <div className="lg:col-span-2 rounded-2xl card-premium p-5 space-y-4">
          <div className="flex items-center gap-2 hairline-b pb-3">
            <TrendingUp className="h-4 w-4 text-accent" />
            <h3 className="font-display text-sm font-bold text-ink">Entrega</h3>
          </div>

          <div className="space-y-4">
            {[
              { key: 'sent',      label: 'Enviados',  bar: 'bg-blue-400'   },
              { key: 'delivered', label: 'Entregues', bar: 'bg-emerald-400' },
              { key: 'read',      label: 'Lidos',     bar: 'bg-accent'     },
              { key: 'failed',    label: 'Falhas',    bar: 'bg-danger'     },
            ].map(({ key, label, bar }) => {
              const val = stats[key as keyof typeof stats] as number;
              const pct = stats.total > 0 ? Math.round((val / stats.total) * 100) : 0;
              return (
                <div key={key} className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">{label}</span>
                    <span className="font-mono text-ink-subtle">
                      {val.toLocaleString('pt-BR')}
                      <span className="text-ink-faint ml-1">({pct}%)</span>
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-midnight-4 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${bar} transition-all duration-700`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Throughput */}
          {isRunning && (
            <div className="rounded-xl bg-amber-500/8 border border-amber-500/15 px-4 py-3 flex items-center gap-3">
              <Zap className="h-4 w-4 text-amber-300 shrink-0" />
              <div>
                <p className="font-display text-lg font-extrabold text-amber-200 tabular-nums leading-none">
                  {throughput}
                </p>
                <p className="text-[10px] text-amber-300/70 mt-0.5">msgs/min (último minuto)</p>
              </div>
            </div>
          )}
        </div>

        {/* Feed ao vivo */}
        <div className="lg:col-span-3 rounded-2xl card-premium p-5 flex flex-col">
          <div className="flex items-center gap-2.5 hairline-b pb-3 shrink-0">
            {/* Dot animado */}
            <div className="relative h-3.5 w-3.5 shrink-0 flex items-center justify-center">
              {isRunning && isConnected && (
                <span className="absolute inset-0 rounded-full bg-accent/30 animate-ping" />
              )}
              <span className="relative h-2.5 w-2.5 rounded-full bg-accent block" />
            </div>
            <h3 className="font-display text-sm font-bold text-ink">Feed ao vivo</h3>
            {isRunning && isConnected && (
              <span className="ml-auto flex items-center gap-1 text-[10px] font-bold text-accent animate-pulse">
                <Radio className="h-3 w-3" />
                LIVE
              </span>
            )}
          </div>

          <div
            ref={feedRef}
            className="flex-1 overflow-y-auto space-y-1.5 pt-3 max-h-80 pr-0.5"
          >
            {events.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                {isRunning
                  ? <>
                      <Loader2 className="h-5 w-5 animate-spin text-accent" />
                      <p className="text-sm text-ink-muted">Aguardando eventos…</p>
                    </>
                  : <p className="text-sm text-ink-faint">Nenhum evento registrado.</p>
                }
              </div>
            ) : (
              events.map((ev) => {
                const s = FEED_STYLE[ev.status];
                return (
                  <div
                    key={ev.key}
                    className="flex items-center gap-2.5 rounded-xl bg-midnight-2/50 hairline px-3 py-2 text-xs animate-fade-in"
                  >
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${s.color} ${s.bg}`}>
                      {s.label}
                    </span>
                    <span className="flex-grow truncate font-mono text-ink-muted">
                      {ev.contact_name ?? maskPhone(ev.phone)}
                    </span>
                    {ev.error_title && (
                      <span className="text-[10px] text-danger truncate max-w-[120px]" title={ev.error_title}>
                        {ev.error_title}
                      </span>
                    )}
                    <span className="shrink-0 font-mono text-[10px] text-ink-faint tabular-nums">
                      {ev.ts.toLocaleTimeString('pt-BR', {
                        hour: '2-digit', minute: '2-digit', second: '2-digit',
                      })}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
